/**
 * "Test-E-Mail senden" — one automation's test send (TEST-EMAIL T3).
 *
 * PRESENTATIONAL. Every value shown is the server's readiness answer — the
 * test address, the sender, the template that would be sent, today's quota,
 * the scenarios — or the server's answer to the send; nothing is derived here
 * and nothing is rendered: no preview of the letter, no local template. The
 * words come from `automationTestEmailModel`.
 *
 * NO RECIPIENT INPUT. The e-mail goes to the organisation's test address, set
 * on the E-Mail-Absender page; where it is missing the dialog says so and
 * links there, and offers no send.
 *
 * `ready` IS NOT A PROMISE. It means the stored prerequisites are met; the
 * dialog says that only the send itself shows whether the provider takes it.
 *
 * PESSIMISTIC, like the termination confirmation's send dialog: the send does
 * not close the dialog; while ITS OWN send runs (`locked`) the dialog cannot be
 * dismissed and the button is unavailable. Its answer stays IN the dialog —
 * sent, rejected, unclear or refused — with focus on it, and the send button
 * is gone: a second attempt is a new opening of the dialog, with a fresh read
 * of the quota, never a click away. Only a scenario the server refused keeps
 * the form, because choosing one is how the reader answers it — and only in
 * the dialog that sent it, with its scenario field there; anywhere else the
 * refusal is an outcome like the others, with a word on how to choose.
 *
 * OPENED WHILE AN EARLIER SEND IS STILL ON ITS WAY (the tab was remounted
 * mid-send), it shows that send running (`busy` without `locked`): it can be
 * closed, it offers no second send, and the answer, when it arrives, is shown
 * here like its own.
 */
import React, { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { automationToneClass } from "./automationLabels";
import {
  QUOTA_SCOPE_NOTE,
  TEST_EMAIL_ACTION_LABEL,
  TEST_EMAIL_BUSY_LABEL,
  TEST_EMAIL_LOADING,
  TEST_EMAIL_MARKER_NOTE,
  TEST_EMAIL_NO_REAL_MESSAGE,
  TEST_EMAIL_RECIPIENT_HINT,
  TEST_EMAIL_RUNNING_ELSEWHERE,
  TEST_EMAIL_SAVED_TEMPLATE_NOTE,
  TEST_EMAIL_SCENARIO_HINT,
  TEST_EMAIL_SCENARIO_LABEL,
  TEST_EMAIL_SCENARIO_PLACEHOLDER,
  TEST_EMAIL_SENDING_STATUS,
  dailyLimitExplanation,
  describeLoadError,
  describeSendError,
  outcomeNote,
  quotaText,
  readinessView,
  sentMessage,
  templateText,
  testEmailScenarioLabel,
} from "./automationTestEmailModel";

const SECONDARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-border bg-card px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";
const LINK_BUTTON =
  "inline-flex items-center justify-center self-start rounded-[9px] px-2 py-1.5 text-[12.5px] font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-9";
const BOX = "flex flex-col gap-1.5 rounded-[10px] border p-3 text-[13px] leading-snug";
const OUTCOME_TONES = {
  rejected: "bad",
  refused: "bad",
  indeterminate: "warning",
  error: "warning",
};

/** One label/value row. Addresses have no spaces to wrap at, so they may
 *  break anywhere rather than widen the dialog past a phone screen. */
function Row({ label, value, hint = null }) {
  return (
    <div className="grid grid-cols-1 gap-x-3 gap-y-0.5 sm:grid-cols-[9rem_minmax(0,1fr)]">
      <dt className="text-[12px] text-muted-foreground">{label}</dt>
      <dd className="m-0 min-w-0 [overflow-wrap:anywhere] text-[13px] text-foreground">
        {value}
        {hint ? (
          <span className="block text-[12px] text-muted-foreground">{hint}</span>
        ) : null}
      </dd>
    </div>
  );
}

function SettingsLink({ link, onLeave }) {
  if (!link) return null;
  return (
    <Link to={link.to} onClick={onLeave} className={LINK_BUTTON}>
      {link.label}
    </Link>
  );
}

export default function AutomationTestEmailDialog({
  open,
  onOpenChange,
  onCloseAutoFocus,
  automationLabel,
  readiness,
  attempt,
  busy = false,
  mine = false,
  locked = false,
  onRetryReadiness,
  onSend,
  onScenarioChange,
  onLeave,
}) {
  const [scenarioKey, setScenarioKey] = useState("");
  const scenarioId = useId();
  const scenarioHintId = useId();
  const sendErrorId = useId();
  const outcomeRef = useRef(null);

  // Every opening starts without a choice: the reader picks, nothing is preset.
  useEffect(() => {
    if (open) setScenarioKey("");
  }, [open]);

  const data = readiness.status === "ready" ? readiness.data : null;
  const view = data ? readinessView(data) : null;
  const failure = attempt.status === "failed" ? describeSendError(attempt.error) : null;
  const sent = attempt.status === "sent" ? attempt.result : null;
  // A scenario refusal is answered in the form that chose the scenario: this
  // dialog's own send, its scenario field still in front of the reader. Any
  // other refusal — a send started before this opening, an automation without
  // a scenario field — is an answer like the rest, shown as the outcome, so it
  // is never lost behind a readiness error or a blocker (adversarial review).
  const answerable =
    !!failure && failure.retryable && mine && view?.scenarioRequired === true;
  const finished = !!sent || (!!failure && !answerable);
  const formShown = !!view && view.canSend && !finished && !(busy && !locked);
  const inlineError = answerable ? failure : null;
  const failureNote = outcomeNote(failure);

  // The answer to a send takes the focus, inside the dialog.
  useEffect(() => {
    if (finished && outcomeRef.current) outcomeRef.current.focus();
  }, [finished]);

  const scenarioMissing = !!view && view.scenarioRequired && !scenarioKey;
  const sendDisabled = busy || scenarioMissing;
  const scenarioOf = (key) => {
    const found = view ? view.scenarios.find((s) => s.key === key) : null;
    return testEmailScenarioLabel(found || { key });
  };

  // An answer comes first: it is shown whatever the readiness read is doing —
  // still loading, refused, or not even asked (adversarial review). A dialog
  // that is open when an answer arrives must show it, or the reader would
  // close it unseen.
  let body;
  if (sent) {
    body = (
      <div
        ref={outcomeRef}
        tabIndex={-1}
        role="status"
        className={`${BOX} ${automationToneClass("positive")} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
      >
        <p className="m-0 font-semibold [overflow-wrap:anywhere]">{sentMessage(sent.recipient)}</p>
        <p className="m-0 text-[12.5px]">
          {`Verwendete Vorlage: ${templateText(sent.template_source, sent.template_version)}`}
        </p>
        {sent.scenario_key ? (
          <p className="m-0 text-[12.5px]">{`Beispielszenario: ${scenarioOf(sent.scenario_key)}`}</p>
        ) : null}
      </div>
    );
  } else if (finished) {
    body = (
      <div className="flex flex-col gap-2">
        <div
          ref={outcomeRef}
          tabIndex={-1}
          role="alert"
          className={`${BOX} ${automationToneClass(OUTCOME_TONES[failure.kind])} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
        >
          <p className="m-0 font-semibold">{failure.message}</p>
          {failureNote ? <p className="m-0 text-[12.5px]">{failureNote}</p> : null}
          {failure.dailyLimitNote ? (
            <p className="m-0 text-[12.5px]">{dailyLimitExplanation(data?.daily_limit)}</p>
          ) : null}
        </div>
        <SettingsLink link={failure.settingsLink} onLeave={onLeave} />
      </div>
    );
  } else if (busy && !locked) {
    // A send of this automation started before this opening (the tab was
    // remounted): said plainly, whatever the readiness read is doing.
    body = (
      <div role="status" className={`${BOX} ${automationToneClass("info")}`}>
        <p className="m-0 font-semibold">{TEST_EMAIL_RUNNING_ELSEWHERE}</p>
      </div>
    );
  } else if (readiness.status === "error") {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="m-0 text-[13px] text-destructive">
          {describeLoadError(readiness.error)}
        </p>
        <button type="button" onClick={onRetryReadiness} className={SECONDARY_BUTTON}>
          Erneut versuchen
        </button>
      </div>
    );
  } else if (!view) {
    body = (
      <p role="status" className="m-0 text-[13px] text-muted-foreground">
        {TEST_EMAIL_LOADING}
      </p>
    );
  } else if (!view.canSend) {
    body = (
      <div className="flex flex-col gap-2">
        <div role="status" className={`${BOX} ${automationToneClass("warning")}`}>
          <ul className="m-0 flex list-none flex-col gap-1 p-0 font-semibold">
            {view.blockers.map((blocker) => (
              <li key={blocker.code}>{blocker.message}</li>
            ))}
          </ul>
          {view.dailyLimitReached ? (
            <p className="m-0 text-[12.5px]">{dailyLimitExplanation(data.daily_limit)}</p>
          ) : null}
        </div>
        <SettingsLink link={view.settingsLink} onLeave={onLeave} />
      </div>
    );
  } else {
    const quota = quotaText(data);
    body = (
      <div className="flex flex-col gap-3">
        <dl className="m-0 flex flex-col gap-2">
          <Row label="Automatisierung" value={automationLabel} />
          <Row label="Empfänger" value={data.test_recipient} hint={TEST_EMAIL_RECIPIENT_HINT} />
          {data.sender_name ? <Row label="Absender" value={data.sender_name} /> : null}
          {data.sender_email ? <Row label="Absenderadresse" value={data.sender_email} /> : null}
          {data.reply_to ? <Row label="Antwort an" value={data.reply_to} /> : null}
          <Row label="Vorlage" value={templateText(data.template_source, data.template_version)} />
          {quota ? <Row label="Tageslimit" value={quota} hint={QUOTA_SCOPE_NOTE} /> : null}
        </dl>

        {view.scenarioRequired ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={scenarioId} className="text-[12.5px] font-semibold text-foreground">
              {TEST_EMAIL_SCENARIO_LABEL}
            </label>
            <select
              id={scenarioId}
              value={scenarioKey}
              required
              aria-required="true"
              aria-invalid={inlineError ? "true" : undefined}
              aria-describedby={
                inlineError ? `${scenarioHintId} ${sendErrorId}` : scenarioHintId
              }
              disabled={busy}
              onChange={(event) => {
                setScenarioKey(event.target.value);
                if (onScenarioChange) onScenarioChange();
              }}
              className="min-h-9 w-full min-w-0 rounded-[9px] border border-border bg-card px-2.5 py-1.5 text-[13px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-md:text-base"
            >
              <option value="">{TEST_EMAIL_SCENARIO_PLACEHOLDER}</option>
              {view.scenarios.map((scenario) => (
                <option key={scenario.key} value={scenario.key}>
                  {testEmailScenarioLabel(scenario)}
                </option>
              ))}
            </select>
            <p id={scenarioHintId} className="m-0 text-[12px] text-muted-foreground">
              {TEST_EMAIL_SCENARIO_HINT}
            </p>
          </div>
        ) : null}

        <div className={`${BOX} ${automationToneClass("info")}`}>
          <p className="m-0 font-semibold">{TEST_EMAIL_MARKER_NOTE}</p>
          <p className="m-0 text-[12.5px]">{TEST_EMAIL_SAVED_TEMPLATE_NOTE}</p>
        </div>

        {inlineError ? (
          <p id={sendErrorId} role="alert" className="m-0 text-[13px] text-destructive">
            {inlineError.message}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (locked) return;
        onOpenChange(next);
      }}
    >
      <AlertDialogContent
        onCloseAutoFocus={onCloseAutoFocus}
        aria-busy={busy || undefined}
        className="max-h-[90dvh] overflow-y-auto"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{TEST_EMAIL_ACTION_LABEL}</AlertDialogTitle>
          <AlertDialogDescription>{TEST_EMAIL_NO_REAL_MESSAGE}</AlertDialogDescription>
        </AlertDialogHeader>

        {body}

        {/* Announced while this dialog's own send runs; present before it
            starts, so a screen reader hears the change. A send started before
            this opening is said by the visible notice above instead. */}
        <p role="status" className="sr-only">
          {locked ? TEST_EMAIL_SENDING_STATUS : ""}
        </p>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={locked}>{formShown ? "Abbrechen" : "Schließen"}</AlertDialogCancel>
          {formShown ? (
            // Busy is `aria-disabled`, not `disabled`: the reader just pressed
            // this button, and a natively disabled button drops the focus onto
            // the page body (browser QA). The click handler refuses meanwhile.
            <AlertDialogAction
              disabled={scenarioMissing}
              aria-disabled={busy || undefined}
              aria-busy={busy || undefined}
              aria-describedby={scenarioMissing ? scenarioHintId : undefined}
              className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={(event) => {
                event.preventDefault();
                if (sendDisabled) return;
                onSend(view.scenarioRequired ? scenarioKey : null);
              }}
            >
              {busy ? TEST_EMAIL_BUSY_LABEL : TEST_EMAIL_ACTION_LABEL}
            </AlertDialogAction>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
